import React, { useState } from "react";
import SearchIcon from "@mui/icons-material/Search";
import { IconButton, InputAdornment, TextField } from "@mui/material";
import { focusRing } from "../../../../theme/tokens";

const SearchField = ({
  height = 46,
  value = "",
  sx = {},
  placeholder = "Search",
  handleSearch = () => {},
  handleChange = () => {},
  isHideIcon = false,
  onChange = () => {},
  fullWidth = true,
  ariaLabel = "Search wheels",
  // QA-1261 — optional hard cap on typed/pasted length. Undefined by default so
  // every existing search caller is unaffected; the Create/Edit-Collection Title
  // field passes it (a collection name was otherwise unbounded, maxlength=null).
  maxLength = undefined,
}) => {
  return (
    <TextField
      autoComplete
      fullWidth={fullWidth}
      value={value}
      type="search"
      onChange={(e) => {
        handleChange(e.target.value);
        onChange(e.target.value);
      }}
      onKeyDown={(e) => {
        if (e.key === "Enter") {
          e.preventDefault();
          handleSearch(value);
        }
      }}
      // onBlur={() => handleSearch(value)}
      placeholder={placeholder}
      inputProps={{
        "aria-label": ariaLabel,
        ...(maxLength != null ? { maxLength } : {}),
      }}
      sx={{
        height: `${height}px`,
        ...sx,
        // ISP-9999 (gordon-0916-themes-preview-search-bar-square-corners) —
        // this bgcolor used to sit on the TextField's own root sx (the
        // FormControl), which paints a plain 0-radius rectangle. The actual
        // rounded border lives one level down on .MuiOutlinedInput-root
        // (MUI's theme border-radius via the notchedOutline fieldset), so
        // that FormControl fill showed as white SQUARE corners poking out
        // past the rounded input underneath it — matching-corner-rounding
        // law: an overlapping fill must share the radius of the element it
        // sits behind. Painting the fill on .MuiOutlinedInput-root itself
        // means it always shares that element's own radius, corners
        // included.
        "& .MuiOutlinedInput-root": {
          height: `${height}px`,
          fontSize: "14px",
          bgcolor: "#ffffff",
          boxSizing: "border-box",
        },
        "& .MuiInputBase-input": {
          marginLeft: !isHideIcon ? "-12px" : "0px",
          height: "100%",
          boxSizing: "border-box",
          paddingTop: 0,
          paddingBottom: 0,
        },
        // The visual field is `.MuiOutlinedInput-root`. MUI's inner <input>
        // is a taller padding box (default outlined padding ~16.5px, plus a
        // 40px IconButton), so an outline on the input sits OUTSIDE the
        // 32px chrome — the "border does not hold the field" gap. Paint
        // the ring on the root via :has(:focus-visible) so it hugs the
        // field; kill the input's own ring (must beat the app-wide
        // `:focus-visible:focus-visible` sheet).
        "& .MuiOutlinedInput-root:has(.MuiOutlinedInput-input:focus-visible)": {
          outline: `${focusRing.width}px solid ${focusRing.color}`,
          outlineOffset: 0,
        },
        "& .MuiOutlinedInput-input:focus-visible:focus-visible": {
          outline: "none",
          outlineOffset: 0,
          boxShadow: "none",
        },
        // ISP-9999 (gordon-0831-gallery-search-double-focus-outline) — without
        // this, MUI's own `Mui-focused` state independently repaints the
        // OutlinedInput's `.MuiOutlinedInput-notchedOutline` fieldset border
        // the SAME brand purple as the ring above, but on the WRAPPER box
        // (not the inner <input>). Two differently-sized boxes in the same
        // color read as a broken double outline with a visible gap, instead
        // of the app-wide ring's single contiguous white+purple band. Pin the
        // fieldset border to its resting (non-purple) color at every
        // interaction state so the field-hugging `:has(:focus-visible)`
        // outline above stays the ONE focus indicator on this field. The
        // inner <input> ring is opted out (see the rule above) so the
        // app-wide `focusRing.css` sheet cannot paint a second, larger
        // box around the padding box. Same MUI quirk as
        // Table/RowsPerPageDropdown.jsx.
        //
        // IMPORTANT: `sx` on <TextField> targets the outer FormControl root,
        // but MUI adds the `Mui-focused` class to the INNER
        // `.MuiOutlinedInput-root` div, not to the FormControl root itself.
        // `"&.Mui-focused ..."` (single compound selector on the sx root) can
        // therefore NEVER match on this element and silently does nothing —
        // confirmed live via cascade inspection (matched CSS rules on the
        // focused notchedOutline showed the base/hover sx rules applied, but
        // NOT the `&.Mui-focused` one; the purple
        // `.css-*.Mui-focused .MuiOutlinedInput-notchedOutline` theme rule
        // won instead). Must target `.MuiOutlinedInput-root.Mui-focused` as a
        // DESCENDANT of the sx root instead.
        "& .MuiOutlinedInput-notchedOutline": {
          borderColor: "rgba(0, 0, 0, 0.23)",
        },
        "&:hover .MuiOutlinedInput-notchedOutline": {
          borderColor: "rgba(0, 0, 0, 0.23)",
        },
        "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline":
          {
            borderColor: "rgba(0, 0, 0, 0.23)",
          },
      }}
      InputProps={{
        startAdornment: !isHideIcon && (
          <InputAdornment position="start">
            {/* Decorative search glyph — the input itself carries the label. */}
            <IconButton
              tabIndex={-1}
              disableRipple
              aria-hidden="true"
              size="small"
              sx={{
                marginLeft: "-10px",
                pointerEvents: "none",
                p: 0,
                width: 24,
                height: 24,
              }}
            >
              <SearchIcon style={{ fontSize: "18px" }} />
            </IconButton>
          </InputAdornment>
        ),
      }}
    />
  );
};

export default SearchField;
